import { useForm } from "@tanstack/react-form";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { baseRole, hasRole, type Role } from "@/lib/roles";

/** Vrijednost dropdowna za nalog bez ikakvog pristupa maloprodaji (komercijalista). */
const NO_BASE_ROLE = "none";

interface UserFormValues {
  name: string;
  email: string;
  password: string;
  /** Skup rola: najviše jedna bazna + dodaci. */
  roles: Role[];
}

interface UserFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Korisnik iz API-ja; role se čitaju iz zarezom odvojene `role` kolone. */
  initialValues?: { name?: string; email?: string; role?: string };
  onSubmit: (values: UserFormValues) => Promise<void>;
  loading: boolean;
  isEdit?: boolean;
}

export function UserForm({
  open,
  onOpenChange,
  initialValues,
  onSubmit,
  loading,
  isEdit = false,
}: UserFormProps) {
  const form = useForm({
    defaultValues: {
      name: initialValues?.name ?? "",
      email: initialValues?.email ?? "",
      password: "",
      baseRole: initialValues ? (baseRole(initialValues) ?? NO_BASE_ROLE) : "viewer",
      wholesale: initialValues ? hasRole(initialValues, "veleprodaja") : false,
      dev: initialValues ? hasRole(initialValues, "dev") : false,
    },
    onSubmit: async ({ value }) => {
      const roles: Role[] = [
        ...(value.baseRole === NO_BASE_ROLE ? [] : [value.baseRole as Role]),
        ...(value.wholesale ? (["veleprodaja"] as Role[]) : []),
        ...(value.dev ? (["dev"] as Role[]) : []),
      ];
      await onSubmit({
        name: value.name,
        email: value.email,
        password: value.password,
        roles,
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Uredi korisnika" : "Novi korisnik"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }} className="flex flex-col gap-4">
          <form.Field name="name">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="user-name">Ime</Label>
                <Input
                  id="user-name"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Ime i prezime"
                  required
                />
              </div>
            )}
          </form.Field>

          <form.Field name="email">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  type="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="email@primjer.ba"
                  required
                />
              </div>
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="user-password">Lozinka</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={isEdit ? "Nova lozinka (ostavite prazno za istu)" : "Lozinka"}
                  required={!isEdit}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="baseRole">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="user-role">Uloga</Label>
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                  <SelectTrigger id="user-role">
                    <SelectValue placeholder="Odaberite ulogu" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="manager">Menadžer</SelectItem>
                    <SelectItem value="viewer">Pregledač</SelectItem>
                    <SelectItem value={NO_BASE_ROLE}>Bez pristupa maloprodaji</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="wholesale">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label>Dodatni pristup</Label>
                <label className="flex items-center gap-2 text-sm font-normal">
                  <Checkbox
                    id="user-wholesale"
                    checked={field.state.value}
                    onCheckedChange={(v) => field.handleChange(v === true)}
                  />
                  Veleprodaja
                </label>
              </div>
            )}
          </form.Field>

          <form.Field name="dev">
            {(field) => (
              <label className="-mt-2 flex items-center gap-2 text-sm font-normal">
                <Checkbox
                  id="user-dev"
                  checked={field.state.value}
                  onCheckedChange={(v) => field.handleChange(v === true)}
                />
                Dev
                <span className="text-xs text-muted-foreground">— prijave i odgovori na njih</span>
              </label>
            )}
          </form.Field>

          <form.Subscribe selector={(s) => [s.values.baseRole, s.values.wholesale] as const}>
            {([base, wholesale]) =>
              base === NO_BASE_ROLE && !wholesale ? (
                <p className="text-sm text-destructive">Odaberite bar jednu ulogu.</p>
              ) : null
            }
          </form.Subscribe>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Otkaži
            </Button>
            <form.Subscribe selector={(s) => [s.values.baseRole, s.values.wholesale] as const}>
              {([base, wholesale]) => (
            <Button type="submit" disabled={loading || (base === NO_BASE_ROLE && !wholesale)}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? "Sačuvaj izmjene" : "Dodaj korisnika"}
            </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
